import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { COLLECTIONS } from '../firebase/collections';
import { notifyNewUser } from '../services/notificationService';
import {
  User, Mail, Lock, Phone, Eye, EyeOff, AlertCircle,
  CheckCircle, Crown, ArrowRight, UserPlus
} from 'lucide-react';

const UserRegister = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (formData.password.length < 6) {
      setError('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('كلمة المرور غير متطابقة');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      await updateProfile(userCredential.user, {
        displayName: formData.name
      });

      const customerData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        createdAt: Timestamp.now(),
        totalOrders: 0
      };

      await setDoc(doc(db, COLLECTIONS.CUSTOMERS, userCredential.user.uid), customerData);

      await notifyNewUser({ id: userCredential.user.uid, ...customerData });

      navigate('/');
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('البريد الإلكتروني مستخدم بالفعل');
      } else {
        setError('حدث خطأ أثناء التسجيل. حاول مرة أخرى.');
      }
      console.error('Registration error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-yellow-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4 py-12">
        <div className="w-full max-w-5xl">

          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-white/20">
            <div className="grid lg:grid-cols-2">

              {/* Left Side - Form */}
              <div className="p-8 md:p-12">

                {/* Logo */}
                <div className="flex items-center justify-center gap-3 mb-8">
                  <img src="/3.png" alt="شعار الموقع" className="w-8 h-8 text-black object-contain" />
                  <h1 className="text-2xl font-bold text-gray-900">LUXURY CAR</h1>
                </div>

                {/* Header */}
                <div className="text-center mb-8">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                    إنشاء حساب جديد
                  </h2>
                  <p className="text-gray-600">
                    انضم إلينا واستمتع بخدماتنا المميزة
                  </p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-5">

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 text-right">
                      الاسم الكامل
                    </label>
                    <div className="relative">
                      <User className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl
                                 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent
                                 text-right transition-all"
                        placeholder="أدخل اسمك الكامل"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 text-right">
                      البريد الإلكتروني
                    </label>
                    <div className="relative">
                      <Mail className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl
                                 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent
                                 text-right transition-all"
                        placeholder="example@email.com"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 text-right">
                      رقم الهاتف
                    </label>
                    <div className="relative">
                      <Phone className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl
                                 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent
                                 text-right transition-all"
                        placeholder="+966 XXX XXXX"
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 text-right">
                      كلمة المرور
                    </label>
                    <div className="relative">
                      <Lock className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        className="w-full pr-12 pl-12 py-3 border-2 border-gray-200 rounded-xl
                                 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent
                                 text-right transition-all"
                        placeholder="6 أحرف على الأقل"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 text-right">
                      تأكيد كلمة المرور
                    </label>
                    <div className="relative">
                      <Lock className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                        className="w-full pr-12 pl-12 py-3 border-2 border-gray-200 rounded-xl
                                 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent
                                 text-right transition-all"
                        placeholder="أعد إدخال كلمة المرور"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700
                             text-white font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-[1.02]
                             disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl
                             flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        جاري إنشاء الحساب...
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-5 h-5" />
                        إنشاء حساب
                      </>
                    )}
                  </button>
                </form>

                {/* Login Link */}
                <div className="mt-6 text-center">
                  <p className="text-gray-600">
                    لديك حساب بالفعل؟{' '}
                    <Link
                      to="/login"
                      className="text-yellow-600 hover:text-yellow-700 font-bold transition-colors inline-flex items-center gap-1"
                    >
                      سجل دخولك
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </p>
                </div>

                {/* Back to Home */}
                <div className="mt-4 text-center">
                  <Link
                    to="/"
                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors inline-flex items-center gap-1"
                  >
                    العودة للصفحة الرئيسية
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>

              {/* Right Side - Benefits */}
              <div className="hidden lg:block bg-gradient-to-br from-yellow-500 to-orange-600 p-12 text-white">
                <div className="h-full flex flex-col justify-center space-y-8">

                  <div>
                    <h3 className="text-3xl font-bold mb-4">مميزات الانضمام</h3>
                    <p className="text-yellow-100">
                      احصل على تجربة مميزة مع خدماتنا الحصرية
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-white/20 p-2 rounded-lg flex-shrink-0">
                        <CheckCircle className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg mb-1">طلبات سريعة</h4>
                        <p className="text-sm text-yellow-100">
                          احفظ معلوماتك واطلب بنقرة واحدة
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="bg-white/20 p-2 rounded-lg flex-shrink-0">
                        <CheckCircle className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg mb-1">تتبع الطلبات</h4>
                        <p className="text-sm text-yellow-100">
                          راقب حالة طلباتك لحظة بلحظة
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="bg-white/20 p-2 rounded-lg flex-shrink-0">
                        <CheckCircle className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg mb-1">عروض حصرية</h4>
                        <p className="text-sm text-yellow-100">
                          خصومات ومميزات خاصة للأعضاء
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="bg-white/20 p-2 rounded-lg flex-shrink-0">
                        <CheckCircle className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg mb-1">سجل كامل</h4>
                        <p className="text-sm text-yellow-100">
                          احتفظ بسجل صيانة سيارتك
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-auto pt-8 border-t border-white/20">
                    <p className="text-sm text-yellow-100">
                      انضم إلى آلاف العملاء الراضين عن خدماتنا
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default UserRegister;
